import React, { useCallback, useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { Box, Popover, PopoverTrigger, PopoverContent, PseudoBox, Flex } from '@chakra-ui/core'
import { Plus, X } from 'lucide-react'

const PRESET_COLORS = ['#F56565', '#ED8936', '#ECC94B', '#48BB78', '#4299E1', '#805AD5', '#D53F8C', '#A0AEC0']

interface ColorPickerProps {
  color: string
  onChange: (color: string) => void
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showHexPicker, setShowHexPicker] = useState(false)
  const [localColor, setLocalColor] = useState(color)

  const handleClose = useCallback(
    (selected?: string) => {
      const finalColor = selected ?? localColor
      setIsOpen(false)
      setShowHexPicker(false)
      onChange(finalColor)
    },
    [localColor, onChange]
  )

  const handleColorSelect = (selectedColor: string) => {
    setLocalColor(selectedColor)
    handleClose(selectedColor)
  }

  const handleRemoveColor = () => {
    setLocalColor('')
    onChange('')
  }

  return (
    <Box position="relative" display="flex" alignItems="center" style={{ gap: '4px' }}>
      <Popover isOpen={isOpen} onClose={handleClose} placement="right-end" closeOnBlur={true}>
        <PopoverTrigger>
          <Box
            minWidth="150px"
            height="40px"
            w="100%"
            borderRadius="10px"
            cursor="pointer"
            position="relative"
            overflow="hidden"
            onClick={() => setIsOpen(true)}
          >
            {localColor ? (
              <Box width="100%" height="100%" backgroundColor={localColor} />
            ) : (
              <Box
                width="100%"
                height="100%"
                backgroundImage="linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)"
                backgroundSize="8px 8px"
                backgroundPosition="0 0, 0 4px, 4px -4px, -4px 0px"
              />
            )}
          </Box>
        </PopoverTrigger>
        <PopoverContent
          width="auto"
          _focus={{ boxShadow: 'none' }}
          zIndex={1000}
          p={2}
          w={showHexPicker ? { base: '100%', md: '350px' } : 'max-content'}
          borderRadius="10px"
        >
          <Flex flexWrap="wrap" style={{ gap: '4px' }} flexDirection="row">
            {PRESET_COLORS.map((presetColor) => (
              <PseudoBox
                key={presetColor}
                width="20px"
                height="20px"
                borderRadius="md"
                cursor="pointer"
                backgroundColor={presetColor}
                transition="transform 0.2s"
                onClick={() => handleColorSelect(presetColor)}
                _hover={{
                  transform: 'scale(1.1)',
                }}
              />
            ))}
            <PseudoBox
              display={showHexPicker ? 'none' : 'flex'}
              width="20px"
              height="20px"
              borderRadius="md"
              cursor="pointer"
              alignItems="center"
              justifyContent="center"
              transition="transform 0.2s"
              onClick={() => setShowHexPicker(true)}
              _hover={{
                transform: 'scale(1.1)',
              }}
            >
              <Plus size={16} color="gray" />
            </PseudoBox>
          </Flex>

          {showHexPicker && (
            <Box mt={2}>
              <HexColorPicker color={localColor} onChange={setLocalColor} />
            </Box>
          )}
        </PopoverContent>
      </Popover>

      {localColor && (
        <PseudoBox
          width="20px"
          height="20px"
          borderRadius="md"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          transition="all 0.2s"
          onClick={handleRemoveColor}
          _hover={{
            transform: 'scale(1.1)',
          }}
        >
          <X size={16} color="red" />
        </PseudoBox>
      )}
    </Box>
  )
}
